 import * as React from 'react';
import { v4 as uuidv4 } from "uuid";
import { SearchResults } from './model/searchresultModel';

import Form from "./components/form";
import Tabs from './components/tabs';
import LoadingApple from './components/loadingApple';
import FilterBox from './components/filterbox';
import FilterPills from './components/filterpills';
import RecipeSearchResults from './components/recipeSearchResults';
import ArticleSearchResults from './components/articleSearchResults';
import IngredientSearchResults from './components/ingredientSearchResults';
import SortSelector from './components/sortselector';
import NoHits from "./components/noHits";

const Search: React.FC = () => {

    const [loading, setLoading] = React.useState(false);
    const [loadingRecipes, setLoadingRecipes] = React.useState(false);
    const [searchResult, setSearchResult] = React.useState<SearchResults>();
    const [activeTab, setActiveTab] = React.useState<number>(
        Number(new URLSearchParams(window.location.search).get('tab')) || 1
    );
    const [mobileFilterVisible, setMobileFilterVisible] = React.useState(false);
    const [mobileFilterArticleVisible, setMobileFilterArticleVisible] = React.useState(false);
    const [mobileFilterIngredientVisible, setMobileFilterIngredientVisible] = React.useState(false);

    const [selectedSort, setSelectedSort] = React.useState('relevance');
    
    const [mounted, setMounted] = React.useState(false);
    
    const [searchTerm, setSearchTerm] = React.useState<string>(
        new URLSearchParams(window.location.search).get('q') ?? 'initial_search' // Default 'inital_search' if value is ''
    );

    const [recipePage, setRecipePage] = React.useState<number>(
        Number(new URLSearchParams(window.location.search).get('recipePage')) || 1 // Default 1 if value is 0 or NULL
    );

    const [articlePage, setArticlePage] = React.useState(
        Number(new URLSearchParams(window.location.search).get('articlePage')) || 1 // Default 1 if value is 0 or NULL
    );
    
    const [ingredientPage, setIngredientPage] = React.useState(
        Number(new URLSearchParams(window.location.search).get('ingredientPage')) || 1 // Default 1 if value is 0 or NULL
    );

    const [pageSize] = React.useState(
        Number(new URLSearchParams(window.location.search).get('pageSize')) || 12 // Default 12 if value is 0 or NULL 
    );    
    
    const [selectedFilterCategory, setSelectedFilterCategory] = React.useState<FilterState>(
        {
            'meal': (new URLSearchParams(window.location.search).get('meal') || "").split(",").map(v => v.trim()).filter(v => v !== "").map(Number).filter(n => Number.isInteger(n) && n > 0),
            'type-of-dish': (new URLSearchParams(window.location.search).get('type-of-dish') || "").split(",").map(v => v.trim()).filter(v => v !== "").map(Number).filter(n => Number.isInteger(n) && n > 0),
            'situation': (new URLSearchParams(window.location.search).get('situation') || "").split(",").map(v => v.trim()).filter(v => v !== "").map(Number).filter(n => Number.isInteger(n) && n > 0),
            'article': (new URLSearchParams(window.location.search).get('article') || "").split(",").map(v => v.trim()).filter(v => v !== "").map(Number).filter(n => Number.isInteger(n) && n > 0),
            'ingredientCategory': (new URLSearchParams(window.location.search).get('ingredientCategory') || "").split(",").map(v => v.trim()).filter(v => v !== "").map(Number).filter(n => Number.isInteger(n) && n > 0)
        }
    )

    const scrollTopRecipe = React.useRef<HTMLDivElement | null>(null);

    // Handle search
    const handleSearch = async (searchTerm: string) => {
        setSearchTerm(searchTerm);

        let params = '';

        // Search term. Eg. eple
        if (searchTerm.length > 0) {
            params += `?q=${searchTerm}`;
        } else {
            params += `?q=initial_search`;
        }
        
        // Add filters value to search
        for (const [key, value] of Object.entries(selectedFilterCategory)) {
            if (value.length > 0) {
                params += `&${key}=${value.join(",")}`;         
            }
        }
        
        params += `&tab=${activeTab}`;
        params += `&sort=${selectedSort}`;
        params += `&recipePage=${recipePage}`;
        params += `&ingredientPage=${ingredientPage}`;
        params += `&articlePage=${articlePage}`;

        // Update URL in browser
        window.history.replaceState({}, "", params);
        
        params += `&pageSize=${pageSize}`;

        try {
            const response = await fetch(`/api/search${params}`, {
                method: 'GET',
                headers: {
                'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                throw new Error('Something went wrong');
            }

            if (response.ok) {
                const data = await response.json(); 
                setSearchResult(data);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setLoadingRecipes(false);
        }
    };

    // Handle filter toggle
    const handleToggleFilter = (key: string, value: number) => {
        setSelectedFilterCategory((prev: FilterState) => {
            // const exists = prev[key].includes(value);
            const exists = prev[key]?.includes?.(value) ?? false;
            
            return {
                ...prev,
                [key]: exists
                    ? prev[key].filter(( v: number) => v !== value)
                    : [...prev[key], value],
            };                
        });

        setRecipePage(1);
        setArticlePage(1);
        setIngredientPage(1);
    };

    // Handle reset filter
    type FilterState = {
        meal: number[];
        'type-of-dish': number[];
        situation: number[];
        [key: string]: number[];
    };
    const handleResetFilter = () => {
        setSelectedFilterCategory(() => {
            const cleared: FilterState = {
                meal: [],
                'type-of-dish': [],
                situation: [],
                article: []
            };
            return cleared;
        });        
        
        setRecipePage(1);
        setArticlePage(1);
        setIngredientPage(1);
    }

    // Scroll to
    const scrollToRecipeTop = () => {
        scrollTopRecipe.current?.scrollIntoView({ behavior: 'smooth' });
    };    
    
    React.useEffect(() => {
        if (mounted) {
            setLoading(true);
            handleSearch(searchTerm);
        }
    }, [searchTerm]);
    
    // Set loading local to recipes
    React.useEffect(() => {
        setLoadingRecipes(true);
        scrollToRecipeTop();
        if (mounted) {
            handleSearch(searchTerm);
        }
    }, [selectedFilterCategory, selectedSort, recipePage, articlePage, ingredientPage]);

    React.useEffect(() => {
        setMounted(true);

        handleSearch(searchTerm);
    }, []);

    // Update tab-param in URL on tab changes
    React.useEffect(() => {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        params.set('tab', activeTab.toString()); // Update the 'tab' parameter

        history.pushState(null, '', url.href);
    }, [activeTab]);
    
    return (
        <>
            {/* Search field */}
            <Form onSearch={handleSearch} value={searchTerm} />

            {/* Loading indicator */}
            {loading && (
                <LoadingApple />
            )}

            {/* Navigation tabs */}
            {!loading && searchResult && (
                <div ref={scrollTopRecipe}>
                    <Tabs result={searchResult} activeTab={activeTab} onChange={setActiveTab}/>
                </div>
            )}

            {/* Search results for recipies */}
            {!loading && searchResult && activeTab === 1 && (
                <div className="flex gap-20">
                    {/* Filters */}
                    <aside className="hidden laptop:block laptop:w-1/4">
                        {searchResult.recipeSearchResult.searchFacets.map((filter) => {
                            return (
                                <FilterBox key={uuidv4()} filter={filter} onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} loading={loadingRecipes} />
                            );
                        })}
                    </aside>

                    <section className="w-full laptop:w-3/4">

                        <header className='flex flex-col laptop:flex-row laptop:justify-between laptop:items-start'>
                            <h2 className='font-semibold text-6xl laptop:text-7xl mb-5 laptop:mb-6'>
                                Oppskrifter
                            </h2>
                            
                            <div className="w-full laptop:w-1/4">

                                {/* Mobile filter pane */}
                                <div className={`bg-brown-50 p-4 rounded-lg mb-4 laptop:hidden ${mobileFilterVisible ? 'block' : 'hidden'}`}>
                                    
                                    <div className='flex items-center justify-between mb-5'>
                                        <h3 className='text-2xl font-semibold'>Filtrer oppskrifter</h3>
                                        
                                        <button className='btn-ghost' onClick={() => setMobileFilterVisible(false)}>
                                            <span className='icon-x' aria-hidden='true'></span> Skjul filtre
                                        </button>
                                    </div>
                                    
                                    <FilterPills onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} searchFasets={searchResult.recipeSearchResult.searchFacets} resetFilter={handleResetFilter} />

                                    <div className="flex flex-col divide-brown-secondary divide-y">
                                        {searchResult.recipeSearchResult.searchFacets.map((filter) => {
                                            return (
                                                <div key={uuidv4()}>
                                                    <div className="-mb-6 -mx-4">
                                                        <FilterBox filter={filter} onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} loading={loadingRecipes} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                
                                <div className='flex justify-between items-center'>
                                    <div className="w-1/2 laptop:w-full">
                                        <SortSelector onSelect={setSelectedSort} selectedSort={selectedSort}/>                                    
                                    </div>
                                    
                                    <div className={`w-1/2 justify-end laptop:hidden ${mobileFilterVisible ? 'hidden' : 'flex'}`}>
                                        <button type="button" className="btn-ghost" onClick={() => setMobileFilterVisible(!mobileFilterVisible)}>
                                            <span className='icon-list-filter' aria-hidden='true'></span> Filter
                                        </button>
                                    </div>
                                </div>

                            </div>
                            
                        </header>

                        {searchTerm !== 'initial_search' && searchTerm.length > 0 && (<p className="italic mb-2">Treff i søk på '{searchTerm}'</p>)}

                        {searchResult.recipeSearchResult.totalMatching > 0 && (
                            <div className="hidden laptop:block">
                                <FilterPills searchFasets={searchResult.recipeSearchResult.searchFacets} selectedFilterCategory={selectedFilterCategory} resetFilter={handleResetFilter} onToggle={handleToggleFilter} />
                            </div>
                        )}
                        

                        {/* Loading indicator */}
                        {loadingRecipes && (
                            <LoadingApple />
                        )}

                        {searchResult.recipeSearchResult.totalMatching === 0 && !loadingRecipes && (
                            <div className="mt-10">
                                <NoHits searchTerm={searchTerm} />
                            </div>
                        )}

                        {searchResult.recipeSearchResult.totalMatching > 0 && (
                            <>
                                {!loadingRecipes && (
                                    <>
                                        {(searchResult.recipeSearchResult.totalMatching > searchResult.recipeSearchResult.pages.length) && (searchResult.recipeSearchResult.pages.length > 1) && (
                                            <p className="my-6">Side {recipePage} av {Math.round(searchResult.recipeSearchResult.totalMatching / 12)}</p>
                                        )}
        
                                        {searchResult.recipeSearchResult.pages.length > 0 && (
                                            <RecipeSearchResults result={searchResult} />
                                        )}
        
                                        {(searchResult.recipeSearchResult.totalMatching > searchResult.recipeSearchResult.pages.length) && (searchResult.recipeSearchResult.pages.length > 1) && (
                                            <div className='mt-10 flex justify-center items-center gap-4'>
                                                <button type="button" className='btn-primary' onClick={() => setRecipePage(recipePage - 1)} disabled={recipePage === 1 ? true : false}>
                                                    Forrige
                                                </button>
        
                                                <div>
                                                    Side {recipePage} av {Math.round(searchResult.recipeSearchResult.totalMatching / 12)}
                                                </div>
        
                                                <button type="button" className='btn-primary' onClick={() => setRecipePage(recipePage + 1)}>
                                                    Neste
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </section>
                </div>
            )}

            {/* Search results for articles */}
            {!loading && searchResult && activeTab === 2 && (
                <>
                    {searchResult.articleSearchResult.totalMatching === 0 && (
                        <NoHits searchTerm={searchTerm} />
                    )}
    
                    {searchResult.articleSearchResult.totalMatching > 0 && (
                        <div className="flex gap-20">
                            <aside className="hidden laptop:block laptop:w-1/4">
                                <h2 className="text-2xl font-semibold mb-2">
                                    Kategorier
                                </h2>
                                {searchResult.articleSearchResult.searchFacets.map((filter) => {
                                    return (
                                        <FilterBox key={uuidv4()} filter={filter} onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} loading={loadingRecipes} />
                                    );
                                })}
                            </aside>
                            
                            
                            <section className="w-full laptop:w-3/4">
        
                                <header className='flex flex-col laptop:flex-row laptop:justify-between laptop:items-start'>
                                    <h2 className='font-semibold text-6xl laptop:text-7xl mb-5 laptop:mb-6'>
                                        Artikler
                                    </h2>
        
                                    <div className="w-full laptop:w-1/4">
                                        {/* Mobile filter pane */}
                                        <div className={`bg-brown-50 p-4 rounded-lg mb-4 laptop:hidden ${mobileFilterArticleVisible ? 'block' : 'hidden'}`}>

                                            <div className='flex items-center justify-between mb-5'>
                                                <h3 className='text-2xl font-semibold'>Filtrer artikler</h3>

                                                <button className='btn-ghost' onClick={() => setMobileFilterArticleVisible(false)}>
                                                    <span className='icon-x' aria-hidden='true'></span> Skjul filtre
                                                </button>
                                            </div>

                                            <FilterPills onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} searchFasets={searchResult.articleSearchResult.searchFacets} resetFilter={handleResetFilter} />

                                            <div className="flex flex-col divide-brown-secondary divide-y">
                                                {searchResult.articleSearchResult.searchFacets.map((filter) => {
                                                    return (
                                                        <div key={uuidv4()}>
                                                            <div className="-mb-6 -mx-4">
                                                                <FilterBox filter={filter} onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} loading={loadingRecipes} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className='flex justify-between items-center'>
                                            <div className="w-1/2 laptop:w-full">
                                                <SortSelector onSelect={setSelectedSort} selectedSort={selectedSort}/>
                                            </div>

                                            <div className={`w-1/2 justify-end laptop:hidden ${mobileFilterArticleVisible ? 'hidden' : 'flex'}`}>
                                                <button type="button" className="btn-ghost" onClick={() => setMobileFilterArticleVisible(!mobileFilterVisible)}>
                                                    <span className='icon-list-filter' aria-hidden='true'></span> Filter
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </header>

                                {searchTerm !== 'initial_search' && searchTerm.length > 0 && (<p className="italic my-2">Treff i søk på '{searchTerm}'</p>)}
                                
                                {/* Loading indicator */}
                                {loadingRecipes && (
                                    <LoadingApple />
                                )}
        
                                {!loadingRecipes && (
                                    <>
                                        {searchResult.articleSearchResult.pages.length > 0 && (
                                            <p className="mb-6">Side {articlePage} av {Math.round(searchResult.articleSearchResult.totalMatching / 12)}</p>
                                        )}
        
                                        {searchResult.articleSearchResult.pages.length > 0 && (
                                            <ArticleSearchResults result={searchResult} />
                                        )}
        
                                        {searchResult.articleSearchResult.totalMatching > searchResult.articleSearchResult.pages.length && (
                                            <div className='mt-10 flex justify-center items-center gap-4'>
                                                <button type="button" className='btn-primary' onClick={() => setArticlePage(articlePage - 1)} disabled={articlePage === 1 ? true : false}>
                                                    Forrige
                                                </button>
        
                                                <div>
                                                    Side {articlePage} av {Math.round(searchResult.articleSearchResult.totalMatching / 12)}
                                                </div>
        
                                                <button type="button" className='btn-primary' onClick={() => setArticlePage(articlePage + 1)}>
                                                    Neste
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>
                        </div>
                    )}
                </>
            )}


            {/* Search results for ingredients */}
            {!loading && searchResult && activeTab === 3 && (
                <div className='flex gap-20'>
                    <aside className="hidden laptop:block laptop:w-1/4">
                        <h2 className="text-2xl font-semibold mb-2">
                            Kategorier
                        </h2>
                        {searchResult.ingredientSearchResult.searchFacets.map((filter) => {
                            return (
                                <FilterBox key={uuidv4()} filter={filter} onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} loading={loadingRecipes} />
                            );
                        })}
                    </aside>
                    
                    <section className="w-full laptop:w-3/4">
                        {searchResult.ingredientSearchResult.totalMatching === 0 && (
                            <NoHits searchTerm={searchTerm} />
                        )}
    
                        {searchResult.ingredientSearchResult.totalMatching > 0 && (
                            <section className="w-full">
                                <header className='flex flex-col laptop:flex-row laptop:justify-between laptop:items-start'>
                                    <h2 className='font-semibold text-6xl laptop:text-7xl mb-5 laptop:mb-6'>
                                        Råvarer
                                    </h2>

                                    <div className="w-full laptop:w-1/4">

                                        {/* Mobile filter pane */}
                                        <div className={`bg-brown-50 p-4 rounded-lg mb-4 laptop:hidden ${mobileFilterIngredientVisible ? 'block' : 'hidden'}`}>

                                            <div className='flex items-center justify-between mb-5'>
                                                <h3 className='text-2xl font-semibold'>Filtrer råvarer</h3>

                                                <button className='btn-ghost' onClick={() => setMobileFilterIngredientVisible(false)}>
                                                    <span className='icon-x' aria-hidden='true'></span> Skjul filtre
                                                </button>
                                            </div>

                                            <FilterPills onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} searchFasets={searchResult.ingredientSearchResult.searchFacets} resetFilter={handleResetFilter} />

                                            <div className="flex flex-col divide-brown-secondary divide-y">
                                                {searchResult.ingredientSearchResult.searchFacets.map((filter) => {
                                                    return (
                                                        <div key={uuidv4()}>
                                                            <div className="-mb-6 -mx-4">
                                                                <FilterBox filter={filter} onToggle={handleToggleFilter} selectedFilterCategory={selectedFilterCategory} loading={loadingRecipes} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div className='flex justify-between items-center'>
                                            <div className="w-1/2 laptop:w-full">
                                                <SortSelector onSelect={setSelectedSort} selectedSort={selectedSort}/>
                                            </div>

                                            <div className={`w-1/2 justify-end laptop:hidden ${mobileFilterIngredientVisible ? 'hidden' : 'flex'}`}>
                                                <button type="button" className="btn-ghost" onClick={() => setMobileFilterIngredientVisible(!mobileFilterIngredientVisible)}>
                                                    <span className='icon-list-filter' aria-hidden='true'></span> Filter
                                                </button>
                                            </div>
                                        </div>

                                    </div>

                                </header>
    
                                {searchTerm !== 'initial_search' && searchTerm.length > 0 && (<p className="italic my-2">Treff i søk på '{searchTerm}'</p>)}
                                
                                {/* Loading indicator */}
                                {loadingRecipes && (
                                    <LoadingApple />
                                )}
        
                                {!loadingRecipes && (
                                    <>
                                        {searchResult.ingredientSearchResult.totalMatching > searchResult.ingredientSearchResult.pages.length && (
                                            <p className="mb-6">Side {ingredientPage} av {Math.round(searchResult.ingredientSearchResult.totalMatching / 12)}</p>
                                        )}
        
                                        {searchResult.ingredientSearchResult.pages.length > 0 && (
                                            <IngredientSearchResults result={searchResult} />
                                        )}
        
                                        {searchResult.ingredientSearchResult.totalMatching > searchResult.ingredientSearchResult.pages.length && (
                                            <div className='mt-10 flex justify-center items-center gap-4'>
                                                <button type="button" className='btn-primary' onClick={() => setIngredientPage(ingredientPage - 1)} disabled={ingredientPage === 1 ? true : false}>
                                                    Forrige
                                                </button>
        
                                                <div>
                                                    Side {ingredientPage} av {Math.round(searchResult.ingredientSearchResult.totalMatching / 12)}
                                                </div>
        
                                                <button type="button" className='btn-primary' onClick={() => setIngredientPage(ingredientPage + 1)}>
                                                    Neste
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>
                        )}
                    </section>
                </div>
            )}            
            
            
        </>
    );
};

export default Search;